import React from 'react'
import {
  IconButton,
  LinearProgress,
  makeStyles,
  Typography,
} from '@material-ui/core'
import { useSelector } from 'react-redux'
import { Link } from 'react-admin'
import LaunchIcon from '@material-ui/icons/Launch'

import { TEntityName, Entity } from '@obvious.tech/constellation/Entity'
import { isPrintable } from '@obvious.tech/util/string'

import { IInputProps } from 'src/types'
import { selectIsEntityMicroserviceAvailable } from 'src/redux/api/api.selectors'
import AutocompleteInput from '../Autocomplete/AutocompleteInput'
import {
  TOption,
  TUseReferenceSelectionParam,
  useReferenceSelection,
} from './useReferenceSelection'
import { useTranslation } from 'react-i18next'

type TValueTArg = string | Record<string, unknown>

type TProps<
  Value extends TValueTArg = TValueTArg,
  Target extends TEntityName = TEntityName,
> = Partial<
  Pick<TUseReferenceSelectionParam<Value, Target>, 'getOptionLabel'>
> &
  IInputProps<Value, Target>

function ReferencedInput<
  Value extends TValueTArg = TValueTArg,
  Target extends TEntityName = TEntityName,
>({
  id,
  property,
  name,
  initialValue,
  onChange,
  onValidationError,
  onBlur,
  value,
  error,
  classes,
  getOptionLabel: getOptionLabelFromProps,
  touched,
  ...props
}: TProps<Value, Target>): JSX.Element {
  const reference = property.reference
  if (typeof reference !== 'object' || reference === null)
    throw new Error('Invalid arguments.')

  const { t } = useTranslation()
  const styles = useStyles()

  const isRelatedMicroserviceAvailable = useSelector(
    selectIsEntityMicroserviceAvailable(reference.target),
  )
  if (!isRelatedMicroserviceAvailable)
    throw new Error(
      `A required entity's microservice is unavailable: ${reference.target}.`,
    )

  const label = property.displayedName ?? name

  const {
    loading,
    inputText,
    selectedOption,
    options,
    optionsLoading,
    handleChange,
    handleInputChange,
    getOptionLabel,
    getOptionDisabled,
    matchInputToOption,
  } = useReferenceSelection({
    initialValue,
    reference,
    onChange,
    getOptionLabel: getOptionLabelFromProps,
  })

  return (
    <span id={id}>
      <AutocompleteInput<TOption<Value, Target>>
        className={classes}
        options={options.unwrapOr([])}
        optionsLoading={optionsLoading}
        optionEq={optionEq}
        input={inputText}
        required={property.required}
        selectedOption={selectedOption}
        onChange={handleChange}
        onValidationError={onValidationError}
        onBlur={onBlur}
        onInputChange={handleInputChange}
        getOptionLabel={getOptionLabel}
        matchInput={matchInputToOption}
        touched={touched}
        renderOption={(option, state) => {
          const labelContent = isPrintable(option.label) ? (
            option.label
          ) : isPrintable(option.id) ? (
            t('global.ID', { id: option.id })
          ) : (
            <em>{t('global.noContent')}</em>
          )

          return (
            <div
              className={styles.option}
              aria-disabled={getOptionDisabled(option)}
              aria-selected={state.selected}
            >
              <Typography
                className={styles.left}
                variant='body1'
                noWrap
              >
                {labelContent}
              </Typography>
              {typeof option.id === 'string' && (
                <ViewOptionLink option={option} />
              )}
            </div>
          )
        }}
        getOptionDisabled={getOptionDisabled}
        disabled={property.readOnly}
        loading={loading}
        label={label}
        placeholder={name}
        error={error}
      />
      {optionsLoading && <LinearProgress />}
    </span>
  )
}

function optionEq(
  a: TOption<TValueTArg, TEntityName> | null,
  b: TOption<TValueTArg, TEntityName> | null,
): boolean {
  return (
    (typeof a?.id === 'string' || typeof b?.id === 'string') && a?.id === b?.id
  )
}

function ViewOptionLink({
  option,
}: {
  option: TOption<TValueTArg, TEntityName> | null
}): JSX.Element | null {
  const styles = useStyles()
  const { t } = useTranslation()

  if (option === null) return null

  return (
    <Link
      to={`/${Entity.getResource(option.entityType)}/${option.id}`}
      target='_blank'
    >
      <IconButton
        className={styles.linkButton}
        onClick={e => {
          e.stopPropagation()
        }}
        // seems to be a typescript bug causing `IconButton` props to fail ?
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        title={t('global.viewDetails') as string}
      >
        <LaunchIcon fontSize='inherit' />
      </IconButton>
    </Link>
  )
}

const useStyles = makeStyles(theme => ({
  option: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  linkButton: {
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.primary.main,
    },
    padding: 0,
    flexShrink: 0,
    display: 'inline-flex',
    verticalAlign: 'middle',
  },
  left: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginRight: theme.spacing(1),
    flex: '1 1 auto',
  },
}))

export default ReferencedInput
